/**
 * Documentation Search Component
 *
 * Full-text search across EnergyPlus documentation using MiniSearch.
 *
 * Features:
 * - Result count display
 * - Search history with recent searches
 * - Category/type filtering
 * - Keyboard navigation
 * - Query term highlighting
 */

import { useState, useCallback, useRef, useEffect, useMemo, forwardRef } from 'react';
import { useDocsStore } from '../store/docsStore';
import type { DocsSearchResult } from '../types';
import { cn } from '@/lib/utils';
import { SearchIcon, FileIcon, FieldIcon, BookIcon, ClockIcon, XIcon, FilterIcon } from './icons';

// Max items to show in dropdown
const MAX_RESULTS = 15;

// Max recent searches to store
const MAX_RECENT_SEARCHES = 5;

interface DocsSearchProps {
  onSelect: (result: DocsSearchResult) => void;
  placeholder?: string;
  autoFocus?: boolean;
  className?: string;
  /** Filter search to a specific type */
  filterType?: 'object' | 'field' | 'engineering';
}

export const DocsSearch = forwardRef<HTMLInputElement, DocsSearchProps>(function DocsSearch(
  { onSelect, placeholder = 'Search documentation...', autoFocus = false, className, filterType },
  ref
) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilter, setActiveFilter] = useState<'object' | 'field' | 'engineering' | null>(
    filterType || null
  );
  const [recentSearches, setRecentSearches] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('docs-recent-searches');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const internalInputRef = useRef<HTMLInputElement>(null);
  const inputRef = (ref as React.RefObject<HTMLInputElement>) || internalInputRef;
  const resultsRef = useRef<HTMLDivElement>(null);

  const searchDocs = useDocsStore((state) => state.searchDocs);
  const searchDocsFiltered = useDocsStore((state) => state.searchDocsFiltered);
  const isSearchReady = useDocsStore((state) => state.isSearchReady);

  // Save recent searches to localStorage
  useEffect(() => {
    if (recentSearches.length > 0) {
      localStorage.setItem('docs-recent-searches', JSON.stringify(recentSearches));
    }
  }, [recentSearches]);

  // Add to recent searches
  const addToRecentSearches = useCallback((searchQuery: string) => {
    if (!searchQuery.trim()) return;
    setRecentSearches((prev) => {
      const filtered = prev.filter((s) => s.toLowerCase() !== searchQuery.toLowerCase());
      return [searchQuery, ...filtered].slice(0, MAX_RECENT_SEARCHES);
    });
  }, []);

  // Clear a recent search
  const removeRecentSearch = useCallback((searchQuery: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setRecentSearches((prev) => prev.filter((s) => s !== searchQuery));
  }, []);

  // Perform search with optional filter
  const results = useMemo(() => {
    if (!query.trim() || !isSearchReady) return [];

    if (activeFilter) {
      return searchDocsFiltered(query, { type: activeFilter, limit: MAX_RESULTS });
    }

    return searchDocs(query, MAX_RESULTS);
  }, [query, searchDocs, searchDocsFiltered, isSearchReady, activeFilter]);

  // Total count (for showing "and X more" message)
  const totalCount = useMemo(() => {
    if (!query.trim() || !isSearchReady) return 0;

    // Get a larger result set to count total
    const allResults = activeFilter
      ? searchDocsFiltered(query, { type: activeFilter, limit: 100 })
      : searchDocs(query, 100);

    return allResults.length;
  }, [query, searchDocs, searchDocsFiltered, isSearchReady, activeFilter]);

  // Auto-focus when requested
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus, inputRef]);

  // Open dropdown when results are available or show recent searches
  useEffect(() => {
    if (results.length > 0) {
      setIsOpen(true);
      setSelectedIndex(0);
    } else if (!query.trim() && recentSearches.length > 0 && document.activeElement === inputRef.current) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [results, query, recentSearches.length, inputRef]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        inputRef.current &&
        !inputRef.current.contains(e.target as Node) &&
        resultsRef.current &&
        !resultsRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setShowFilters(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [inputRef]);

  const handleSelect = useCallback(
    (result: DocsSearchResult) => {
      addToRecentSearches(query);
      onSelect(result);
      setQuery('');
      setIsOpen(false);
    },
    [onSelect, query, addToRecentSearches]
  );

  const handleRecentSearchClick = useCallback(
    (searchQuery: string) => {
      setQuery(searchQuery);
      inputRef.current?.focus();
    },
    [inputRef]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Handle filter toggle with Ctrl+F
      if (e.key === 'f' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setShowFilters((prev) => !prev);
        return;
      }

      if (!isOpen) {
        // Open recent searches on arrow down when empty
        if (e.key === 'ArrowDown' && !query.trim() && recentSearches.length > 0) {
          setIsOpen(true);
          return;
        }
        return;
      }

      const itemCount = results.length > 0 ? results.length : recentSearches.length;
      if (itemCount === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((i) => (i + 1) % itemCount);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((i) => (i - 1 + itemCount) % itemCount);
          break;
        case 'Enter':
          e.preventDefault();
          if (results.length > 0 && results[selectedIndex]) {
            handleSelect(results[selectedIndex]);
          } else if (!query.trim() && recentSearches[selectedIndex]) {
            handleRecentSearchClick(recentSearches[selectedIndex]);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setShowFilters(false);
          break;
      }
    },
    [isOpen, results, selectedIndex, handleSelect, query, recentSearches, handleRecentSearchClick]
  );

  const getResultIcon = (type: string) => {
    switch (type) {
      case 'object':
        return <FileIcon className="h-4 w-4 text-blue-500" />;
      case 'field':
        return <FieldIcon className="h-4 w-4 text-green-500" />;
      case 'engineering':
        return <BookIcon className="h-4 w-4 text-purple-500" />;
      default:
        return <FileIcon className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getResultTypeLabel = (type: string) => {
    switch (type) {
      case 'object':
        return 'Object';
      case 'field':
        return 'Field';
      case 'engineering':
        return 'Engineering';
      default:
        return type;
    }
  };

  // Highlight query terms in snippet
  const highlightQuery = (text: string, searchQuery: string) => {
    if (!searchQuery.trim()) return text;

    const queryWords = searchQuery
      .toLowerCase()
      .split(/\s+/)
      .filter((w) => w.length > 1);
    if (queryWords.length === 0) return text;

    // Create regex pattern for all query words
    const pattern = new RegExp(
      `(${queryWords.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`,
      'gi'
    );

    const parts = text.split(pattern);

    return parts.map((part, i) => {
      const isMatch = queryWords.some((w) => part.toLowerCase() === w.toLowerCase());
      if (isMatch) {
        return (
          <mark key={i} className="bg-yellow-200 dark:bg-yellow-800 rounded px-0.5">
            {part}
          </mark>
        );
      }
      return part;
    });
  };

  const handleFilterChange = (filter: 'object' | 'field' | 'engineering' | null) => {
    setActiveFilter(filter);
    setSelectedIndex(0);
  };

  return (
    <div className={cn('relative', className)}>
      <div className="relative">
        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (results.length > 0 || (!query.trim() && recentSearches.length > 0)) {
              setIsOpen(true);
            }
          }}
          placeholder={placeholder}
          className={cn(
            'w-full pl-10 pr-10 py-2 text-sm rounded-md border border-input',
            'bg-background text-foreground placeholder:text-muted-foreground',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent'
          )}
          role="combobox"
          aria-expanded={isOpen}
          aria-controls="docs-search-results"
          aria-autocomplete="list"
        />
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {!isSearchReady && (
            <span className="text-xs text-muted-foreground">Indexing...</span>
          )}
          {isSearchReady && !filterType && (
            <button
              onClick={() => setShowFilters((prev) => !prev)}
              className={cn(
                'p-1 rounded hover:bg-muted transition-colors',
                (showFilters || activeFilter) && 'text-primary'
              )}
              title="Filter search (Ctrl+F)"
              type="button"
            >
              <FilterIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Filter pills */}
      {showFilters && !filterType && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-popover border border-border rounded-md shadow-lg p-2">
          <div className="text-xs text-muted-foreground mb-2">Filter by type:</div>
          <div className="flex flex-wrap gap-1">
            <button
              onClick={() => handleFilterChange(null)}
              className={cn(
                'text-xs px-2 py-1 rounded-full transition-colors',
                activeFilter === null
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/80'
              )}
            >
              All
            </button>
            <button
              onClick={() => handleFilterChange('object')}
              className={cn(
                'text-xs px-2 py-1 rounded-full transition-colors flex items-center gap-1',
                activeFilter === 'object'
                  ? 'bg-blue-500 text-white'
                  : 'bg-muted hover:bg-muted/80'
              )}
            >
              <FileIcon className="h-3 w-3" />
              Objects
            </button>
            <button
              onClick={() => handleFilterChange('field')}
              className={cn(
                'text-xs px-2 py-1 rounded-full transition-colors flex items-center gap-1',
                activeFilter === 'field'
                  ? 'bg-green-500 text-white'
                  : 'bg-muted hover:bg-muted/80'
              )}
            >
              <FieldIcon className="h-3 w-3" />
              Fields
            </button>
            <button
              onClick={() => handleFilterChange('engineering')}
              className={cn(
                'text-xs px-2 py-1 rounded-full transition-colors flex items-center gap-1',
                activeFilter === 'engineering'
                  ? 'bg-purple-500 text-white'
                  : 'bg-muted hover:bg-muted/80'
              )}
            >
              <BookIcon className="h-3 w-3" />
              Engineering
            </button>
          </div>
        </div>
      )}

      {/* Results dropdown */}
      {isOpen && (results.length > 0 || (!query.trim() && recentSearches.length > 0)) && (
        <div
          ref={resultsRef}
          id="docs-search-results"
          className={cn(
            'absolute left-0 right-0 top-full mt-1 z-50',
            'bg-popover border border-border rounded-md shadow-lg',
            'max-h-80 overflow-y-auto',
            showFilters && 'mt-14'
          )}
          role="listbox"
        >
          {/* Recent searches (when no query) */}
          {!query.trim() && recentSearches.length > 0 && (
            <>
              <div className="px-3 py-2 text-xs text-muted-foreground border-b flex items-center gap-1">
                <ClockIcon className="h-3 w-3" />
                Recent Searches
              </div>
              {recentSearches.map((search, index) => (
                <button
                  key={search}
                  onClick={() => handleRecentSearchClick(search)}
                  className={cn(
                    'w-full flex items-center justify-between gap-3 px-3 py-2 text-left',
                    'hover:bg-muted transition-colors group',
                    selectedIndex === index && 'bg-muted'
                  )}
                  role="option"
                  aria-selected={selectedIndex === index}
                >
                  <span className="text-sm text-foreground truncate">{search}</span>
                  <button
                    onClick={(e) => removeRecentSearch(search, e)}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-muted-foreground/20 rounded transition-opacity"
                    title="Remove from history"
                  >
                    <XIcon className="h-3 w-3 text-muted-foreground" />
                  </button>
                </button>
              ))}
            </>
          )}

          {/* Search results */}
          {results.length > 0 && (
            <>
              {/* Result count header */}
              <div className="px-3 py-2 text-xs text-muted-foreground border-b flex items-center justify-between">
                <span>
                  {totalCount === results.length
                    ? `${totalCount} result${totalCount !== 1 ? 's' : ''}`
                    : `Showing ${results.length} of ${totalCount} results`}
                </span>
                {activeFilter && (
                  <button
                    onClick={() => setActiveFilter(null)}
                    className="text-primary hover:underline flex items-center gap-1"
                  >
                    Clear filter
                    <XIcon className="h-3 w-3" />
                  </button>
                )}
              </div>

              {results.map((result, index) => (
                <button
                  key={result.id}
                  onClick={() => handleSelect(result)}
                  className={cn(
                    'w-full flex items-start gap-3 px-3 py-2 text-left',
                    'hover:bg-muted transition-colors',
                    selectedIndex === index && 'bg-muted'
                  )}
                  role="option"
                  aria-selected={selectedIndex === index}
                >
                  <div className="mt-0.5 shrink-0">{getResultIcon(result.type)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm text-foreground truncate">
                        {result.title}
                      </span>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground shrink-0">
                        {getResultTypeLabel(result.type)}
                      </span>
                    </div>
                    {result.group && (
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{result.group}</p>
                    )}
                    {result.snippet && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {highlightQuery(result.snippet, query)}
                      </p>
                    )}
                  </div>
                </button>
              ))}

              {/* More results indicator */}
              {totalCount > results.length && (
                <div className="px-3 py-2 text-xs text-muted-foreground text-center border-t">
                  +{totalCount - results.length} more results. Refine your search for better
                  matches.
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* No results message */}
      {isOpen && query.trim() && results.length === 0 && isSearchReady && (
        <div
          className={cn(
            'absolute left-0 right-0 top-full mt-1 z-50 bg-popover border border-border rounded-md shadow-lg p-4 text-center text-sm text-muted-foreground',
            showFilters && 'mt-14'
          )}
        >
          <p>No results found for "{query}"</p>
          {activeFilter && (
            <button
              onClick={() => setActiveFilter(null)}
              className="mt-2 text-primary hover:underline text-xs"
            >
              Try searching all categories
            </button>
          )}
        </div>
      )}
    </div>
  );
});

/**
 * Compact search for inline use
 */
export function DocsSearchCompact({
  onSelect,
  className,
}: {
  onSelect: (result: DocsSearchResult) => void;
  className?: string;
}) {
  return (
    <DocsSearch onSelect={onSelect} placeholder="Search..." className={cn('max-w-xs', className)} />
  );
}
